<template>
  <section class="hero-section bg-[#111] text-white py-8 sm:py-12 lg:py-20 overflow-hidden" aria-labelledby="hero-heading">
    <div class="max-w-screen-xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center">

      <!-- Text kolom -->
      <div>
        <p class="hero-eyebrow text-[0.8rem] sm:text-[0.875rem] font-semibold text-[#FFD500] mb-3 sm:mb-5 tracking-[0.02em]">
          <span aria-hidden="true">⚡</span>
          Zonnepanelen · Laadpalen · Batterijen
        </p>

        <h1 id="hero-heading" class="hero-title text-[clamp(1.75rem,5vw,3.5rem)] font-black leading-[1.08] mb-3 sm:mb-5 text-white">
          Van zonnepaneel tot laadpaal.
          <span class="text-[#FFD500]">Eén adres.</span>
        </h1>

        <p class="hero-body text-[0.9375rem] sm:text-[1.125rem] leading-[1.7] text-[#ccc] mb-5 sm:mb-9 max-w-[520px]">
          Eerlijk geprijsd, snel geleverd, met 2&nbsp;jaar garantie.
          <span class="hidden sm:inline">Al&nbsp;vanaf&nbsp;€299.</span>
        </p>

        <div class="hero-buttons flex gap-3 flex-wrap mb-4 sm:mb-5 max-md:flex-col">
          <router-link to="/producten" class="btn-primary max-md:justify-center">
            <i class="bi bi-lightning-charge"></i>
            Bekijk producten
          </router-link>
          <router-link to="/hoe-het-werkt" class="btn-ghost max-md:justify-center hidden sm:inline-flex">
            <i class="bi bi-question-circle"></i>
            Hoe het werkt
          </router-link>
        </div>

        <!-- Waarom Plug2Charge? — USP-checklist (bootman-stijl) -->
        <div class="hero-usps">
          <p class="hero-usps-title">Waarom Plug2Charge?</p>
          <ul class="hero-usps-list" role="list">
            <li><i class="bi bi-check-circle-fill" aria-hidden="true"></i> Voor 15:00 besteld, morgen in huis</li>
            <li><i class="bi bi-check-circle-fill" aria-hidden="true"></i> Gratis verzending vanaf €50</li>
            <li><i class="bi bi-check-circle-fill" aria-hidden="true"></i> 2 jaar garantie op alle producten</li>
            <li><i class="bi bi-check-circle-fill" aria-hidden="true"></i> 30 dagen retour, niet goed geld terug</li>
            <li class="hidden sm:flex"><i class="bi bi-check-circle-fill" aria-hidden="true"></i> A-merken uit eigen voorraad</li>
          </ul>
        </div>
      </div>

      <!-- Visual - verborgen op mobiel -->
      <div
        class="hero-visual relative hidden lg:block"
        aria-hidden="true"
        @mouseenter="pause"
        @mouseleave="resume"
      >
        <!-- Roterende media: video's en/of afbeeldingen wisselen elkaar af.
             Voeg items toe of pas ze aan in de `slides`-array in <script setup>.
             preload="none": video's laden pas op desktop wanneer ze actief worden. -->
        <div class="hero-stage relative w-full aspect-[4/3] rounded-[20px] overflow-hidden bg-black/20">
          <transition name="hero-fade">
            <video
              v-if="current.type === 'video'"
              :key="index"
              :src="current.src"
              autoplay
              muted
              loop
              playsinline
              preload="none"
              class="absolute inset-0 w-full h-full object-cover"
            />
            <img
              v-else
              :key="index"
              :src="current.src"
              :alt="current.alt"
              loading="lazy"
              class="absolute inset-0 w-full h-full object-cover"
            />
          </transition>
        </div>

        <!-- Indicatoren -->
        <div
          v-if="slides.length > 1"
          class="hero-dots absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2 z-10"
        >
          <button
            v-for="(s, i) in slides"
            :key="i"
            type="button"
            tabindex="-1"
            class="hero-dot"
            :class="{ 'is-active': i === index }"
            :aria-label="`Ga naar slide ${i + 1}`"
            :aria-current="i === index ? 'true' : undefined"
            @click="go(i)"
          ></button>
        </div>

        <!-- Badge top -->
        <div class="hero-badge badge-top absolute top-[-1.25rem] right-[-1rem]">
          <span class="text-[1.75rem] leading-none">🌿</span>
          <div>
            <strong class="block text-[#111] text-[0.9375rem] font-bold">100% Groen</strong>
            <small class="block text-[#444] text-[0.75rem]">Duurzame energie</small>
          </div>
        </div>

        <!-- Badge bottom -->
        <div class="hero-badge badge-bottom absolute bottom-[-1rem] left-[-1rem]">
          <span class="text-[1.75rem] leading-none">💰</span>
          <div>
            <strong class="block text-[#111] text-[0.9375rem] font-bold">Tot 40% besparing</strong>
            <small class="block text-[#444] text-[0.75rem]">Op je energierekening</small>
          </div>
        </div>
      </div>

    </div>
  </section>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

// ── Roterende hero-visuals ──────────────────────────────────────────────
// Voeg hier eenvoudig visuals toe of pas ze aan.  type: 'video' of 'image'.
const slides = [
  { type: 'video', src: 'https://cdn.shopify.com/videos/c/o/v/b4b885441ff7434a9be0f93c29bdf2ef.mp4', alt: 'Plug2Charge energie in actie' },
  { type: 'image', src: '/wp-content/uploads/2026/07/balkonpanelen_met_batterij.png', alt: 'Balkon zonnepanelen met thuisbatterij' },
  { type: 'image', src: 'https://nl.jackery.com/cdn/shop/files/KV4_S3_Pro_Max_AC_NL_PC.jpg?v=1774266671&width=800', alt: 'Jackery thuisbatterij' },
]

const ROTATE_MS = 6000
const index = ref(0)
const current = computed(() => slides[index.value])

const reduceMotion =
  typeof window !== 'undefined' &&
  window.matchMedia?.('(prefers-reduced-motion: reduce)').matches

let timer = null
function next() { index.value = (index.value + 1) % slides.length }
function start() {
  if (reduceMotion || slides.length < 2 || timer) return
  timer = setInterval(next, ROTATE_MS)
}
function stop() { if (timer) { clearInterval(timer); timer = null } }
function go(i) { index.value = i; stop(); start() }
const pause = stop
const resume = start

onMounted(start)
onBeforeUnmount(stop)
</script>

<style scoped>
/* ─── Entrance animaties ─── */
@keyframes fadeUp {
  from { opacity: 0; transform: translateY(28px); }
  to   { opacity: 1; transform: translateY(0); }
}

@keyframes fadeInRight {
  from { opacity: 0; transform: translateX(36px); }
  to   { opacity: 1; transform: translateX(0); }
}

.hero-eyebrow {
  animation: fadeUp 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.05s;
}
.hero-title {
  animation: fadeUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.18s;
}
.hero-body {
  animation: fadeUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.3s;
}
.hero-buttons {
  animation: fadeUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.42s;
}
.hero-micro {
  animation: fadeUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.50s;
}
.hero-stats {
  animation: fadeUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.54s;
}
/* ─── Waarom Plug2Charge? — USP-checklist (bootman-stijl) ─── */
.hero-usps {
  animation: fadeUp 0.6s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.54s;
}
.hero-usps-title {
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #FFD500;
  margin: 0 0 0.75rem;
}
.hero-usps-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 0.55rem;
}
.hero-usps-list li {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 0.9rem;
  color: #e5e5e5;
  line-height: 1.4;
}
.hero-usps-list li i {
  color: #FFD500;
  font-size: 1rem;
  flex-shrink: 0;
}
.hero-visual {
  animation: fadeInRight 0.7s cubic-bezier(0.22, 1, 0.36, 1) both;
  animation-delay: 0.2s;
}

/* ─── Roterende visual: crossfade + indicatoren ─── */
.hero-fade-enter-active,
.hero-fade-leave-active { transition: opacity 0.6s ease; }
.hero-fade-enter-from,
.hero-fade-leave-to { opacity: 0; }

.hero-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: none;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.45);
  cursor: pointer;
  transition: background 0.2s ease, width 0.2s ease;
}
.hero-dot:hover { background: rgba(255, 255, 255, 0.7); }
.hero-dot.is-active { width: 22px; background: #FFD500; }

/* ─── Floating badges ─── */
.hero-badge {
  background: rgba(255, 255, 255, 0.96);
  backdrop-filter: blur(10px);
  padding: 0.875rem 1.25rem;
  border-radius: 12px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.badge-top {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) both, float 4s ease-in-out 0.8s infinite;
}

.badge-bottom {
  animation: fadeUp 0.5s cubic-bezier(0.22, 1, 0.36, 1) both, float 4s ease-in-out 2.8s infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0); }
  50%       { transform: translateY(-8px); }
}

/* ─── Knoppen ─── */
.btn-primary {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8125rem 1.875rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  background: #FFD500;
  color: #111;
  border: 2px solid transparent;
  box-shadow: 0 2px 12px rgba(255, 213, 0, 0.35);
  transition: background 0.2s ease, transform 0.2s ease, box-shadow 0.2s ease;
  white-space: nowrap;
}
.btn-primary:hover {
  background: #ffb800;
  transform: translateY(-2px);
  box-shadow: 0 6px 20px rgba(255, 213, 0, 0.5);
}

.btn-ghost {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8125rem 1.875rem;
  border-radius: 6px;
  font-weight: 700;
  font-size: 1rem;
  text-decoration: none;
  background: rgba(255, 255, 255, 0.1);
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.28);
  backdrop-filter: blur(6px);
  transition: background 0.2s ease, border-color 0.2s ease;
  white-space: nowrap;
}
.btn-ghost:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.5);
}

/* ─── Ambient diepte (on-brand glow i.p.v. vlak zwart) ─── */
.hero-section {
  background-image:
    radial-gradient(55% 75% at 12% 0%, rgba(255, 213, 0, 0.10), transparent 60%),
    radial-gradient(45% 60% at 100% 100%, rgba(34, 197, 94, 0.07), transparent 60%);
}

/* ─── Pressed + focus states ─── */
.btn-primary:active {
  transform: translateY(0) scale(0.99);
  box-shadow: 0 2px 10px rgba(255, 213, 0, 0.4);
}
.btn-ghost:active {
  transform: translateY(0) scale(0.99);
}
.btn-primary:focus-visible,
.btn-ghost:focus-visible {
  outline: none;
  box-shadow: 0 0 0 3px rgba(255, 213, 0, 0.55);
}

/* ─── Toegankelijkheid ─── */
@media (prefers-reduced-motion: reduce) {
  .hero-eyebrow, .hero-title, .hero-body,
  .hero-buttons, .hero-micro, .hero-stats, .hero-usps, .hero-visual,
  .badge-top, .badge-bottom {
    animation: none;
    opacity: 1;
    transform: none;
  }
  .btn-primary:hover,
  .btn-ghost:hover {
    transform: none;
  }
}
</style>
